import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
    communicationsInsightsWindowLabel,
    type CommunicationsInsightsGranularity,
    type CommunicationsInsightsSeriesPoint
} from '../../../services/communicationsInsights';
import {
    AXIS_TICK,
    CHART_RESIZE_DEBOUNCE_MS,
    HOVER_CURSOR,
    formatBucketLabel
} from '../../../utils/chartFormatting';
import { CommunicationsGranularityToggle } from './CommunicationsGranularityToggle';
import { CardErrorState } from './CardErrorState';
import { ChartSkeleton } from '../../shared/ChartSkeleton';

// Stacked bar chart of incoming/outgoing entries per bucket; granularity
// toggle shared with the rail cards below. No h-full - sized externally.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col';
const INCOMING_COLOR = '#2C6F67';
const OUTGOING_COLOR = '#9ca3af';
const CHART_HEIGHT = 200;
const BAR_RADIUS: [number, number, number, number] = [3, 3, 0, 0];

interface ChartRow {
    bucket: string;
    label: string;
    incoming: number;
    outgoing: number;
    total: number;
}

interface CommunicationsOverTimeTooltipProps {
    active?: boolean;
    payload?: { payload: ChartRow }[];
}

function CommunicationsOverTimeTooltip({ active, payload }: CommunicationsOverTimeTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3.5 py-2.5 min-w-[140px]">
            <p className="text-xs font-bold text-gray-800 mb-1.5">{row.label}</p>
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    <span
                        className="w-2 h-2 rounded-[2px] shrink-0"
                        style={{ background: INCOMING_COLOR }}
                    />
                    <span className="text-[11px] text-gray-500 flex-1">Incoming</span>
                    <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                        {row.incoming}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span
                        className="w-2 h-2 rounded-[2px] shrink-0"
                        style={{ background: OUTGOING_COLOR }}
                    />
                    <span className="text-[11px] text-gray-500 flex-1">Outgoing</span>
                    <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                        {row.outgoing}
                    </span>
                </div>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 mt-1.5 pt-1.5">
                <span className="text-[11px] font-semibold text-gray-600">Total</span>
                <span className="text-[11px] font-bold text-gray-900 tabular-nums">
                    {row.total}
                </span>
            </div>
        </div>
    );
}

interface CommunicationsOverTimeCardProps {
    series: CommunicationsInsightsSeriesPoint[];
    granularity: CommunicationsInsightsGranularity;
    onGranularityChange: (next: CommunicationsInsightsGranularity) => void;
    isLoading: boolean;
    isError?: boolean;
    onRetry?: () => void;
    className?: string;
}

export function CommunicationsOverTimeCard({
    series,
    granularity,
    onGranularityChange,
    isLoading,
    isError = false,
    onRetry,
    className = ''
}: CommunicationsOverTimeCardProps) {
    const rows: ChartRow[] = series.map((point) => ({
        bucket: point.bucket,
        label: formatBucketLabel(point.bucket, granularity),
        incoming: point.incoming,
        outgoing: point.outgoing,
        total: point.total
    }));
    const hasCommunications = rows.some((row) => row.total > 0);

    return (
        <div className={`${CARD_CLASS} ${className}`.trim()}>
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 shrink-0">
                <div className="min-w-0">
                    <h3 className="text-[15px] font-bold text-gray-800">
                        Communications over time
                    </h3>
                    <span className="text-[11px] text-gray-400">
                        {communicationsInsightsWindowLabel(granularity)}
                    </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            <span
                                className="w-2.5 h-2.5 rounded-[3px]"
                                style={{ background: INCOMING_COLOR }}
                            />
                            Incoming
                        </span>
                        <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            <span
                                className="w-2.5 h-2.5 rounded-[3px]"
                                style={{ background: OUTGOING_COLOR }}
                            />
                            Outgoing
                        </span>
                    </div>
                    <CommunicationsGranularityToggle
                        value={granularity}
                        onChange={onGranularityChange}
                    />
                </div>
            </div>
            <div className="flex-1 min-h-0 px-5 py-4" style={{ height: CHART_HEIGHT }}>
                {isLoading ? (
                    <ChartSkeleton />
                ) : isError ? (
                    <CardErrorState
                        message="Couldn't load communications over time"
                        onRetry={() => onRetry?.()}
                    />
                ) : !hasCommunications ? (
                    <div className="h-full flex flex-col items-center justify-center gap-2 text-center">
                        <ChatBubbleLeftRightIcon
                            className="h-8 w-8 text-gray-300"
                            strokeWidth={1}
                        />
                        <p className="text-sm text-gray-400">No communications in this period.</p>
                    </div>
                ) : (
                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                        debounce={CHART_RESIZE_DEBOUNCE_MS}
                        className="overflow-x-clip"
                    >
                        <BarChart data={rows} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f3f4f6"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="label"
                                tick={AXIS_TICK}
                                axisLine={false}
                                tickLine={false}
                                tickMargin={6}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                tick={AXIS_TICK}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <Tooltip
                                cursor={HOVER_CURSOR}
                                content={<CommunicationsOverTimeTooltip />}
                            />
                            <Bar
                                dataKey="incoming"
                                name="Incoming"
                                stackId="communications"
                                fill={INCOMING_COLOR}
                                maxBarSize={28}
                            />
                            <Bar
                                dataKey="outgoing"
                                name="Outgoing"
                                stackId="communications"
                                fill={OUTGOING_COLOR}
                                radius={BAR_RADIUS}
                                maxBarSize={28}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
